import React from "react";
import { cn } from "../../../lib/utils";
import { Radio } from "../../ui/radio";
import QuestionFrame from "./QuestionFrame";

export const LIKERT_SCALES = {
  agree_disagree: [
    "Strongly disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly agree",
  ],
  satisfaction: [
    "Very dissatisfied",
    "Dissatisfied",
    "Neutral",
    "Satisfied",
    "Very satisfied",
  ],
  likelihood: [
    "Very unlikely",
    "Unlikely",
    "Neutral",
    "Likely",
    "Very likely",
  ],
  quality: ["Very poor", "Poor", "Fair", "Good", "Excellent"],
  ease: ["Very difficult", "Difficult", "Neutral", "Easy", "Very easy"],
  hours: ["None", "<1 hr", "2-5 hrs", "6-9 hrs", "10+ hrs"],
};

/**
 * Resolve the five labels for an agree/disagree-style question.
 *
 * @param {object} question
 * @returns {string[]}
 */
export function likertLabels(question) {
  const scaleId = question?.scale;
  const base = LIKERT_SCALES[scaleId] || LIKERT_SCALES.agree_disagree;
  const labels = [...base];
  if (question?.lowLabel) labels[0] = question.lowLabel;
  if (question?.highLabel) labels[labels.length - 1] = question.highLabel;
  return labels;
}

/**
 * Five-point scale (positive / higher end on the right).
 *
 * @param {Object} props
 * @param {object} props.question
 * @param {number} [props.value]
 * @param {function(number): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.showRequired]
 * @returns {JSX.Element}
 */
export default function AgreeDisagreeQuestion({ question, value, onChange, error, readOnly, showRequired }) {
  const name = `survey-q-${question.id}`;
  const options = likertLabels(question).map((label, index) => ({
    value: index + 1,
    label,
  }));

  return (
    <QuestionFrame question={question} error={error} showRequired={showRequired}>
      <fieldset disabled={readOnly} className="min-w-0">
        <legend className="sr-only">{question.prompt}</legend>
        <div className="grid grid-cols-5 items-stretch gap-x-1 sm:gap-x-2">
          {options.map((option) => {
            const checked = value === option.value;
            return (
              <label
                key={option.value}
                className={cn(
                  "flex h-full min-w-0 flex-col items-center",
                  readOnly ? "cursor-default" : "cursor-pointer"
                )}
              >
                <span className="mt-auto text-center text-[11px] leading-tight text-text-secondary sm:text-xs">
                  {option.label}
                </span>
                <Radio
                  name={name}
                  value={String(option.value)}
                  checked={checked}
                  disabled={readOnly}
                  onSelect={() => onChange(option.value)}
                  aria-label={option.label}
                  className="mt-2 h-5 w-5 shrink-0"
                />
              </label>
            );
          })}
        </div>
      </fieldset>
    </QuestionFrame>
  );
}
